import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { authService } from '../auth-config/auth-service';
import { BookInfo, useAuthContext } from '../auth-config';
import shaka from 'shaka-player/dist/shaka-player.compiled.js';
import {
  FiRotateCcw,
  FiPlay,
  FiPause,
  FiCheck,
  FiX,
  FiChevronRight,
  FiHelpCircle,
  FiGrid,
  FiImage,
  FiList,
  FiFileText,
  FiMaximize,
  FiMinimize,
} from 'react-icons/fi';
import ChatWidget from './Chat/ChatWidget';

// KaTeX Math Renderer helper loaded from CDN
export const MathRenderer: React.FC<{ math: string; block?: boolean }> = ({ math, block = false }) => {
  const containerRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const renderMath = () => {
      const katex = (window as any).katex;
      if (katex && containerRef.current) {
        try {
          katex.render(math, containerRef.current, {
            throwOnError: false,
            displayMode: block,
          });
        } catch (err) {
          console.error(err);
        }
      }
    };

    if (!(window as any).katex) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://cdn.jsdelivr.net/npm/katex@0.16.8/dist/katex.min.css';
      document.head.appendChild(link);

      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/katex@0.16.8/dist/katex.min.js';
      script.async = true;
      script.onload = renderMath;
      document.head.appendChild(script);
    } else {
      renderMath();
    }
  }, [math, block]);

  return <span ref={containerRef} />;
};

export const renderTextWithMath = (text: string) => {
  if (!text) return '';
  const parts = text.split(/(\$\$[\s\S]+?\$\$|\$.+?\$)/g);
  return parts.map((part, index) => {
    if (part.startsWith('$$') && part.endsWith('$$')) {
      return <MathRenderer key={index} math={part.slice(2, -2)} block />;
    } else if (part.startsWith('$') && part.endsWith('$')) {
      return <MathRenderer key={index} math={part.slice(1, -1)} />;
    }
    return part;
  });
};

interface PlayerProps {
  videoId: string;
  bookInfo: BookInfo | null
}

const SecureVideoPlayer: React.FC<PlayerProps> = ({ videoId, bookInfo }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const { user } = useAuthContext();

  const savedProgressRef = useRef<{ lastPositionSeconds: number; isCompleted: boolean; durationSeconds: number } | null>(null);
  const hasToastedProgressRef = useRef<boolean>(false);
  const lastSavedTimeRef = useRef<number>(0);
  const playLoggedRef = useRef<boolean>(false);
  const watchTimeRef = useRef<number>(0);
  const lastLoggedTimeRef = useRef<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resolution, setResolution] = useState<'720p' | '480p'>('720p');
  const [downloadProgress, setDownloadProgress] = useState<number>(0);

  // Watermark state
  const [ipAddress, setIpAddress] = useState<string>('Detecting IP...');
  const [watermarkPos, setWatermarkPos] = useState({ top: '20%', left: '20%' });

  // Interactive video states
  const [interactions, setInteractions] = useState<any[]>([]);
  const [activeInteraction, setActiveInteraction] = useState<any | null>(null);
  const [interactedIds, setInteractedIds] = useState<Set<number>>(new Set());
  const [interactionStartTime, setInteractionStartTime] = useState<number>(0);
  const [attemptNumber, setAttemptNumber] = useState<number>(1);
  const [feedback, setFeedback] = useState<{ isEvaluated: boolean; isCorrect: boolean; score: number; maxScore: number } | null>(null);

  // Input states for current interaction
  const [mcSelected, setMcSelected] = useState<string[]>([]);
  const [freeTextVal, setFreeTextVal] = useState<string>('');
  const [fibAnswers, setFibAnswers] = useState<{ [key: number]: string }>({});
  const [ddPlacedItems, setDdPlacedItems] = useState<string[]>([]);
  const [summarySelected, setSummarySelected] = useState<string | null>(null);
  const [scIndex, setScIndex] = useState<number>(0);
  const [scSelected, setScSelected] = useState<{ [key: number]: string }>({});
  const [mwSelected, setMwSelected] = useState<Set<number>>(new Set());
  const [dtPlaced, setDtPlaced] = useState<{ [key: number]: string }>({});
  const [dtSelectedTag, setDtSelectedTag] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [standardId, setStandardId] = useState<number | null>(null);

  const toggleFullscreen = () => {
    if (!containerRef.current) return;
    const fullscreenElement = document.fullscreenElement || (document as any).webkitFullscreenElement;
    if (!fullscreenElement) {
      if (containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen().catch(() => { });
      } else if ((containerRef.current as any).webkitRequestFullscreen) {
        (containerRef.current as any).webkitRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen().catch(() => { });
      } else if ((document as any).webkitExitFullscreen) {
        (document as any).webkitExitFullscreen();
      }
    }
  };

  // Fullscreen event listener to keep HTML overlays visible in fullscreen mode
  useEffect(() => {
    const handleFullscreenChange = () => {
      const fullscreenElement = document.fullscreenElement || (document as any).webkitFullscreenElement;
      if (fullscreenElement === videoRef.current && containerRef.current) {
        if (document.exitFullscreen) {
          document.exitFullscreen().then(() => {
            containerRef.current?.requestFullscreen?.().catch(() => { });
          }).catch(() => { });
        } else if ((document as any).webkitExitFullscreen) {
          (document as any).webkitExitFullscreen();
          (containerRef.current as any)?.webkitRequestFullscreen?.();
        }
      }
      setIsFullscreen(Boolean(fullscreenElement));
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Fetch client IP for watermark
  useEffect(() => {
    fetch('https://api.ipify.org?format=json')
      .then((res) => res.json())
      .then((data) => setIpAddress(data.ip))
      .catch(() => setIpAddress('unknown-ip'));
  }, []);

  // Periodic random shift of the floating watermark to prevent recording/cropping
  useEffect(() => {
    const interval = setInterval(() => {
      const randomTop = Math.floor(Math.random() * 70) + 15; // Keep between 15% and 85%
      const randomLeft = Math.floor(Math.random() * 60) + 15; // Keep between 15% and 75%
      setWatermarkPos({ top: `${randomTop}%`, left: `${randomLeft}%` });
    }, 7000);
    return () => clearInterval(interval);
  }, []);

  // Keyboard shortcut blocking (F12, Inspect, Save)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'i' || e.key === 'J' || e.key === 'j')) ||
        (e.ctrlKey && (e.key === 'U' || e.key === 'u' || e.key === 'S' || e.key === 's'))
      ) {
        e.preventDefault();
        toast.error('Developer tools and saving options are locked.');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Focus and visibility listeners to pause video when switching tabs/windows
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && videoRef.current) {
        videoRef.current.pause();
        toast('Playback paused (page hidden)', { icon: '⏸️' });
      }
    };
    const handleBlur = () => {
      // Use a short timeout to check if the document actually lost focus,
      // preventing false positives from internal element clicks or toast renders.
      setTimeout(() => {
        if (!document.hasFocus() && videoRef.current && !videoRef.current.paused) {
          videoRef.current.pause();
          toast('Playback paused (focus lost)', { icon: '⏸️' });
        }
      }, 250);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleBlur);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);

  // Main Player Init
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    let shakaPlayer: any = null;
    let blobUrl: string | null = null;
    let isMounted = true;

    // Install polyfills to support all EME / DRM features across browsers
    shaka.polyfill.installAll();

    const initializeStreaming = async () => {
      hasToastedProgressRef.current = false;
      try {
        if (isMounted) {
          setLoading(true);
          setError(null);
        }
        
        // 1. Fetch Bearer Token
        const token = await authService.getAccessToken();
        if (!token) {
          throw new Error('Authentication token is missing. Please log in again.');
        }
        if (board && standard && !standardId) {
          try {
            const stdRes = await fetch(
              `/api/student/register/standards?board=${encodeURIComponent(board)}`,
              { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (stdRes.ok) {
              const stds = await stdRes.json();
              const matched = stds.find((s: any) => s.name === standard);
              if (matched?.id) setStandardId(matched.id);
            }
          } catch {
            // standardId stays null — ChatWidget simply won't render
          }
        }
        // Fetch interactive overlay points & saved watch progress
        try {
          const [resInteractions, resProgress] = await Promise.all([
            fetch(`/api/student/videos/${videoId}/interactions`, { headers: { 'Authorization': `Bearer ${token}` } }),
            fetch(`/api/student/videos/${videoId}/progress`, { headers: { 'Authorization': `Bearer ${token}` } })
          ]);

          if (resInteractions.ok) {
            const data = await resInteractions.json();
            setInteractions(data || []);
          }
          if (resProgress.ok) {
            const pData = await resProgress.json();
            savedProgressRef.current = pData;
          }
        } catch (err) {
          console.error('Failed to load interactive overlays or progress', err);
        }

        // 2. Prime CloudFront signed cookies and request the playback URL before loading media.
        // const playbackResponse = await fetch(`/api/videos/${videoId}/playback-url`, {
        //   headers: {
        //     'Authorization': `Bearer ${token}`
        //   },
        //   credentials: 'include'
        // });

        const playbackResponse = await fetch(`/api/videos/${videoId}/playback-url?token=${encodeURIComponent(token || '')}`, {
          credentials: 'include'
        });

        if (!playbackResponse.ok) {
          const errData = await playbackResponse.json().catch(() => ({}));
          throw new Error(errData.message || `Access verification failed (Status: ${playbackResponse.status})`);
        }

        const data = await playbackResponse.json();

        if (data.cookies) {
          const { keyPairId, policy, signature } = data.cookies;
          const secure = window.location.protocol === 'https:' ? '; secure; SameSite=None' : '';
          const domain = window.location.hostname.includes('mastimanch.com') ? '; domain=.mastimanch.com' : '';
          const expires = new Date(Date.now() + 3600 * 1000).toUTCString();

          document.cookie = `CloudFront-Key-Pair-Id=${keyPairId}; path=/; expires=${expires}${secure}${domain}`;
          document.cookie = `CloudFront-Policy=${policy}; path=/; expires=${expires}${secure}${domain}`;
          document.cookie = `CloudFront-Signature=${signature}; path=/; expires=${expires}${secure}${domain}`;
        }

        const streamUrl = data.videoUrl || data.url || `https://content.mastimanch.com/${videoId}.m3u8`;

        const isDASH = streamUrl.includes('.mpd');
        const isHLS = streamUrl.includes('.m3u8');

        // 3. DRM Configuration Setup for streaming (DASH/HLS)
        if ((isDASH || isHLS) && shaka.Player.isBrowserSupported()) {
          const widevineLicenseUrl = data.widevineLicenseUrl || 'https://license.widevine.com/cenc?provider=mastimanch';
          const playreadyLicenseUrl = data.playreadyLicenseUrl || '';
          const fairplayLicenseUrl = data.fairplayLicenseUrl || '';
          const fairplayCertUrl = data.fairplayCertUrl || '';

          let playbackUrl = streamUrl;
          try {
            const manifestRes = await fetch(streamUrl, { credentials: 'include' });
            if (manifestRes.ok) {
              let manifestText = await manifestRes.text();
              manifestText = manifestText.replace(/^(?!\s*#)(?!\s*https?:\/\/)(.*)/gm, (line) => {
                const trimmed = line.trim();
                if (!trimmed) return line;
                return new URL(trimmed, streamUrl).href;
              });
              const manifestBlob = new Blob([manifestText], { type: 'application/x-mpegurl' });
              blobUrl = URL.createObjectURL(manifestBlob);
              playbackUrl = blobUrl;
            }
          } catch (e) {
            console.warn('Could not create Blob URL for manifest, using direct stream URL', e);
          }



          shakaPlayer = new shaka.Player(video);
          shakaPlayer.getNetworkingEngine().registerRequestFilter((type: any, request: any) => {
            request.allowCrossSiteCredentials = true;
          });
          shakaPlayer.addEventListener('error', (event: any) => {
            console.error('Shaka Player error:', event.detail);
          });

          const drmConfig: any = {
            servers: {
              'com.widevine.alpha': widevineLicenseUrl
            }
          };

          if (playreadyLicenseUrl) {
            drmConfig.servers['com.microsoft.playready'] = playreadyLicenseUrl;
          }

          if (fairplayLicenseUrl && fairplayCertUrl) {
            const certResponse = await fetch(fairplayCertUrl);
            const certData = await certResponse.arrayBuffer();
            drmConfig.servers['com.apple.fps.1_0'] = fairplayLicenseUrl;
            drmConfig.advanced = {
              'com.apple.fps.1_0': {
                serverCertificate: new Uint8Array(certData)
              }
            };
          }

          shakaPlayer.configure({
            drm: drmConfig,
            streaming: {
              rebufferingGoal: 3,
            }
          });

          // Apply saved progress directly after Shaka Player loads successfully using Shaka's load startTime param
          let startPosition: number | null = null;
          if (savedProgressRef.current) {
            const { lastPositionSeconds, isCompleted, durationSeconds } = savedProgressRef.current;
            const dur = Math.floor(video.duration || durationSeconds || 0);
            if (dur > 0 && (lastPositionSeconds >= dur - 5 || isCompleted)) {
              startPosition = 0;
            } else if (lastPositionSeconds > 0) {
              startPosition = lastPositionSeconds;
            }
          }

          try {
            await shakaPlayer.load(streamUrl, startPosition, isHLS ? 'application/x-mpegurl' : undefined);
          } catch (loadErr) {
            console.warn('Shaka load with direct stream URL failed, trying Blob URL fallback', loadErr);
            if (blobUrl) {
              await shakaPlayer.load(blobUrl, startPosition, isHLS ? 'application/x-mpegurl' : undefined);
            } else {
              throw loadErr;
            }
          }

          if (startPosition && startPosition > 0 && !hasToastedProgressRef.current) {
            hasToastedProgressRef.current = true;
            const mins = Math.floor(startPosition / 60);
            const secs = ('0' + (startPosition % 60)).slice(-2);
            toast(`Resumed playback from ${mins}:${secs}`, { icon: '⏯️' });
          }

          if (isMounted) setLoading(false);
          return;
        }

        // For standard progressive MP4 / video files, stream directly for instant playback
        if (isMounted) {
          video.src = streamUrl;
          setLoading(false);
        }

      } catch (err: any) {
        console.error('Error starting video stream:', err);
        if (isMounted) {
          setError(err.message || 'Unable to verify video access.');
          toast.error(err.message || 'Unable to verify video access.');
        }
      } finally {
        if (isMounted && !shakaPlayer) {
          setLoading(false);
        }
      }
    };

    initializeStreaming();

    // Clean up
    return () => {
      isMounted = false;
      if (shakaPlayer) {
        shakaPlayer.destroy();
      }
      if (video) {
        video.src = '';
        video.removeAttribute('src');
        try {
          video.load();
        } catch (e) {}
      }
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [videoId, resolution]);

  // Handle Video Viewport Click to Toggle Play/Pause
  const handleVideoClick = (e: React.MouseEvent<HTMLVideoElement>) => {
    if (!videoRef.current) return;
    const rect = videoRef.current.getBoundingClientRect();
    const clickY = e.clientY - rect.top;
    // Only toggle play/pause if click is on the main video screen (above native controls bar)
    if (clickY < rect.height - 50) {
      if (videoRef.current.paused) {
        videoRef.current.play().catch(() => { });
      } else {
        videoRef.current.pause();
      }
    }
  };

  // Save student video progress to DB
  const saveProgress = async (pos: number, isComp: boolean) => {
    try {
      const token = await authService.getAccessToken();
      if (!token || !videoRef.current) return;
      const dur = Math.floor(videoRef.current.duration || 0);
      await fetch(`/api/student/videos/${videoId}/progress`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ lastPositionSeconds: Math.floor(pos), durationSeconds: dur, isCompleted: isComp })
      });
    } catch (e) {
      console.error('Failed to save video progress', e);
    }
  };

  // Handle Loaded Metadata to Auto-Resume or Restart if near end
  const handleLoadedMetadata = () => {
    if (!videoRef.current || !savedProgressRef.current) return;

    // If it's a blob URL (Shaka Player), Shaka Player handles startup seek natively.
    // Manually setting currentTime here will conflict with Shaka's internal seeking and trigger loops.
    if (videoRef.current.src && videoRef.current.src.startsWith('blob:')) {
      return;
    }

    const { lastPositionSeconds, isCompleted, durationSeconds } = savedProgressRef.current;
    const dur = Math.floor(videoRef.current.duration || durationSeconds || 0);

    // Requirement: if video is about to finish or 5 seconds left (or marked completed), start from beginning (0s)
    if (dur > 0 && (lastPositionSeconds >= dur - 5 || isCompleted)) {
      videoRef.current.currentTime = 0;
    } else if (lastPositionSeconds > 0 && !hasToastedProgressRef.current) {
      hasToastedProgressRef.current = true;
      videoRef.current.currentTime = lastPositionSeconds;
      const mins = Math.floor(lastPositionSeconds / 60);
      const secs = ('0' + (lastPositionSeconds % 60)).slice(-2);
      toast(`Resumed playback from ${mins}:${secs}`, { icon: '⏯️' });
    }
  };

  // Video Time Update Listener
  const handleTimeUpdate = () => {
    if (!videoRef.current) return;
    const currentTime = videoRef.current.currentTime;
    const duration = videoRef.current.duration;

    // Auto-save watch position every 5 seconds or when near end
    const isComp = duration > 0 && currentTime >= duration - 5;
    if (Math.abs(currentTime - lastSavedTimeRef.current) >= 5 || isComp) {
      lastSavedTimeRef.current = currentTime;
      saveProgress(currentTime, isComp);
    }

    // Track watch time threshold (15s) in this session
    if (!videoRef.current.paused) {
      if (lastLoggedTimeRef.current !== null) {
        const diff = currentTime - lastLoggedTimeRef.current;
        if (diff > 0 && diff < 2) { // Verify it's played sequentially (not a seek)
          watchTimeRef.current += diff;
          if (watchTimeRef.current >= 15 && !playLoggedRef.current) {
            playLoggedRef.current = true;
            logPlayToBackend();
          }
        }
      }
      lastLoggedTimeRef.current = currentTime;
    } else {
      lastLoggedTimeRef.current = null;
    }

    // Check if there is an interaction matching this start time that wasn't completed yet
    const trigger = interactions.find(
      (item) => currentTime >= item.startTime && (item.endTime ? currentTime <= item.endTime : currentTime <= item.startTime + 5) && !interactedIds.has(item.id)
    );

    if (trigger && activeInteraction?.id !== trigger.id) {
      if (trigger.pauseOnTrigger !== false) {
        if (!videoRef.current.paused) {
          videoRef.current.pause();
        }
      }
      setupInteraction(trigger);
    }
  };

  const setupInteraction = (interaction: any) => {
    setActiveInteraction(interaction);
    setInteractionStartTime(Date.now());
    setAttemptNumber(1);
    setFeedback(null);

    // Reset inputs
    setMcSelected([]);
    setFreeTextVal('');
    setFibAnswers({});
    setDdPlacedItems([]);
    setSummarySelected(null);
    setScIndex(0);
    setScSelected({});
    setMwSelected(new Set());
    setDtPlaced({});
    setDtSelectedTag(null);
  };

  // Skip Interaction handler
  const handleSkip = async () => {
    if (!activeInteraction) return;
    const timeSpent = Math.round((Date.now() - interactionStartTime) / 1000);

    // Log attempt with skipped status
    logInteractionAttempt('skipped', 0, 0, '{"action":"skipped"}', timeSpent);

    setInteractedIds((prev) => new Set([...prev, activeInteraction.id]));
    setActiveInteraction(null);

    // Resume video playback
    if (videoRef.current) {
      videoRef.current.play();
    }
  };

  // Continue Handler
  const handleContinue = () => {
    if (!activeInteraction) return;
    setInteractedIds((prev) => new Set([...prev, activeInteraction.id]));
    setActiveInteraction(null);
    if (videoRef.current) {
      videoRef.current.play();
    }
  };

  // Retry Handler
  const handleRetry = () => {
    setAttemptNumber((prev) => prev + 1);
    setFeedback(null);
    // Reset responses
    setMcSelected([]);
    setFreeTextVal('');
    setFibAnswers({});
    setDdPlacedItems([]);
    setSummarySelected(null);
    setScIndex(0);
    setScSelected({});
    setMwSelected(new Set());
    setDtPlaced({});
    setDtSelectedTag(null);
  };

  // Submit Handler
  const handleSubmit = async () => {
    if (!activeInteraction) return;
    const content = JSON.parse(activeInteraction.contentJson || '{}');
    const type = activeInteraction.interactionType;

    let score = 0;
    let maxScore = 1;
    let responseJson = '';

    if (type === 'multiple_choice') {
      const opts = content.options || [];
      const correctOpts = opts.filter((o: any) => o.correct).map((o: any) => o.text);

      const matchedCorrect = mcSelected.filter(val => correctOpts.includes(val)).length;
      const matchedIncorrect = mcSelected.filter(val => !correctOpts.includes(val)).length;

      score = Math.max(0, matchedCorrect - matchedIncorrect);
      maxScore = correctOpts.length || 1;
      if (score === maxScore && matchedIncorrect === 0) {
        score = maxScore;
      } else {
        score = 0; // standard strict match for simplicity
      }
      responseJson = JSON.stringify({ selected: mcSelected });
    }
    else if (type === 'free_text') {
      score = freeTextVal.trim().length > 0 ? 1 : 0;
      maxScore = 1;
      responseJson = JSON.stringify({ text: freeTextVal });
    }
    else if (type === 'fill_in_the_blanks') {
      const parts = (content.question || '').split(/(\*[^*]+\*)/g);
      const blanks = parts.filter((_: any, idx: number) => idx % 2 === 1).map((s: string) => s.slice(1, -1).trim());
      maxScore = blanks.length || 1;

      let correctCount = 0;
      blanks.forEach((correctVal: string, idx: number) => {
        const studentVal = (fibAnswers[idx] || '').trim();
        if (studentVal.toLowerCase() === correctVal.toLowerCase()) {
          correctCount++;
        }
      });
      score = correctCount;
      responseJson = JSON.stringify({ answers: fibAnswers });
    }
    else if (type === 'drag_drop') {
      const correctItems = content.correctItems && content.correctItems.length > 0
        ? content.correctItems
        : (content.items || []);
      const isCorrect = ddPlacedItems.length === correctItems.length &&
        ddPlacedItems.every(i => correctItems.includes(i));
      score = isCorrect ? 1 : 0;
      maxScore = 1;
      responseJson = JSON.stringify({ placed: ddPlacedItems });
    }
    else if (type === 'summary') {
      const correctStmt = content.statements?.[0] || '';
      score = summarySelected === correctStmt ? 1 : 0;
      maxScore = 1;
      responseJson = JSON.stringify({ selected: summarySelected });
    }
    else if (type === 'single_choice_set') {
      const questionsList = content.questions || [];
      maxScore = questionsList.length || 1;
      let correctCount = 0;
      questionsList.forEach((qItem: any, qIdx: number) => {
        const correctVal = qItem.options?.[0] || '';
        if (scSelected[qIdx] === correctVal) {
          correctCount++;
        }
      });
      score = correctCount;
      responseJson = JSON.stringify({ selectedMap: scSelected });
    }
    else if (type === 'mark_words') {
      const cleanText = (content.question || '');
      const words = cleanText.split(/\s+/);
      const correctIndices: number[] = [];
      words.forEach((w: string, idx: number) => {
        if (w.startsWith('*') && w.endsWith('*')) {
          correctIndices.push(idx);
        }
      });

      maxScore = correctIndices.length || 1;
      let matchedCorrect = 0;
      let matchedIncorrect = 0;
      mwSelected.forEach(idx => {
        if (correctIndices.includes(idx)) {
          matchedCorrect++;
        } else {
          matchedIncorrect++;
        }
      });

      score = Math.max(0, matchedCorrect - matchedIncorrect);
      responseJson = JSON.stringify({ clickedIndices: Array.from(mwSelected) });
    }
    else if (type === 'drag_text') {
      const parts = (content.question || '').split(/(\*[^*]+\*)/g);
      const blanks = parts.filter((_: any, idx: number) => idx % 2 === 1).map((s: string) => s.slice(1, -1).trim());
      maxScore = blanks.length || 1;

      let correctCount = 0;
      blanks.forEach((correctVal: string, idx: number) => {
        if ((dtPlaced[idx] || '').trim().toLowerCase() === correctVal.toLowerCase()) {
          correctCount++;
        }
      });
      score = correctCount;
      responseJson = JSON.stringify({ placedMap: dtPlaced });
    }
    else {
      // Info overlays (image, table, label, text)
      score = 1;
      maxScore = 1;
      responseJson = JSON.stringify({ action: 'viewed' });
    }

    const isCorrect = score === maxScore;
    setFeedback({
      isEvaluated: true,
      isCorrect,
      score,
      maxScore
    });

    const timeSpent = Math.round((Date.now() - interactionStartTime) / 1000);
    const status = isCorrect ? 'correct' : 'incorrect';

    logInteractionAttempt(status, score, maxScore, responseJson, timeSpent);
  };

  // Post attempt log
  const logInteractionAttempt = async (
    status: string,
    score: number,
    maxScore: number,
    responseJson: string,
    timeSpent: number
  ) => {
    try {
      const token = await authService.getAccessToken();
      if (!token) return;

      const payload = {
        VideoId: parseInt(videoId, 10),
        InteractionId: activeInteraction.id,
        InteractionType: activeInteraction.interactionType,
        ResponseStatus: status,
        ScoreObtained: score,
        MaxScore: maxScore,
        StudentResponseJson: responseJson,
        AttemptNumber: attemptNumber,
        TimeSpentSeconds: timeSpent
      };

      await fetch('/api/student/videos/interactions/log', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
    } catch (err) {
      console.error('Failed to log student interaction attempt', err);
    }
  };

  const logPlayToBackend = async () => {
    try {
      const token = await authService.getAccessToken();
      if (!token) return;

      await fetch(`/api/student/videos/${videoId}/log-play`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ resolution })
      });
    } catch (err) {
      console.error('Failed to log watch play', err);
    }
  };

  const renderInteractionContent = () => {
    if (!activeInteraction) return null;
    const content = JSON.parse(activeInteraction.contentJson || '{}');
    const type = activeInteraction.interactionType;
    const isEvaluated = feedback?.isEvaluated || false;

    switch (type) {
      case 'multiple_choice':
        return (
          <div className="space-y-4">
            <h3 className="text-base font-extrabold text-white leading-relaxed">
              {renderTextWithMath(content.question || '')}
            </h3>
            <div className="space-y-2">
              {(content.options || []).map((opt: any, idx: number) => {
                const isSelected = mcSelected.includes(opt.text);
                return (
                  <button
                    key={idx}
                    disabled={isEvaluated}
                    onClick={() => {
                      setMcSelected(prev =>
                        prev.includes(opt.text)
                          ? prev.filter(t => t !== opt.text)
                          : [...prev, opt.text]
                      );
                    }}
                    className={`w-full text-left p-3 rounded-xl border text-sm font-semibold flex items-center justify-between transition ${isSelected
                      ? 'bg-blue-600/35 border-blue-500 text-white'
                      : 'bg-slate-800/40 border-slate-700/60 hover:bg-slate-800 text-slate-300'
                      }`}
                  >
                    <span>{renderTextWithMath(opt.text)}</span>
                    {isEvaluated && opt.correct && (
                      <span className="text-green-400 text-xs font-bold flex items-center gap-1">
                        <FiCheck /> Correct choice
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        );

      case 'free_text':
        return (
          <div className="space-y-3">
            <h3 className="text-base font-extrabold text-white leading-relaxed">
              {renderTextWithMath(content.question || '')}
            </h3>
            <textarea
              disabled={isEvaluated}
              value={freeTextVal}
              onChange={(e) => setFreeTextVal(e.target.value)}
              placeholder="Type your answer here..."
              style={{ backgroundColor: '#1e293b', color: '#f8fafc' }}
              className="w-full border border-slate-700 rounded-xl p-3 text-sm focus:outline-none focus:border-blue-500 h-24"
            />
          </div>
        );

      case 'fill_in_the_blanks': {
        const parts = (content.question || '').split(/(\*[^*]+\*)/g);
        let blankCount = 0;
        return (
          <div className="space-y-4">
            <h3 className="text-sm font-bold text-slate-400">Fill in the missing words:</h3>
            <div className="bg-slate-800/20 border border-slate-800 p-4 rounded-xl text-base leading-loose text-slate-200">
              {parts.map((part: string, idx: number) => {
                if (part.startsWith('*') && part.endsWith('*')) {
                  const currentBlankIdx = blankCount;
                  blankCount++;
                  const studentAns = fibAnswers[currentBlankIdx] || '';
                  const correctVal = part.slice(1, -1).trim();

                  return (
                    <input
                      key={idx}
                      type="text"
                      disabled={isEvaluated}
                      value={studentAns}
                      placeholder="..."
                      onChange={(e) => {
                        const val = e.target.value;
                        setFibAnswers(prev => ({ ...prev, [currentBlankIdx]: val }));
                      }}
                      className={`mx-1.5 px-2 py-0.5 rounded border text-sm font-mono text-center focus:outline-none w-28 bg-slate-900 ${isEvaluated
                        ? studentAns.trim().toLowerCase() === correctVal.toLowerCase()
                          ? 'border-green-500 text-green-300'
                          : 'border-red-500 text-red-300'
                        : 'border-slate-600 focus:border-blue-500 text-white'
                        }`}
                    />
                  );
                }
                return <span key={idx}>{renderTextWithMath(part)}</span>;
              })}
            </div>
          </div>
        );
      }

      case 'drag_drop':
        return (
          <div className="space-y-4">
            <h3 className="text-base font-extrabold text-white leading-relaxed">
              {renderTextWithMath(content.question || '')}
            </h3>
            <div className="bg-blue-900/10 border-2 border-dashed border-blue-500/20 rounded-2xl p-4 text-center">
              <span className="text-[10px] font-bold text-blue-400 uppercase tracking-wider block mb-2">
                Drop Zone: {content.dropZoneLabel || 'Target Box'}
              </span>
              <div className="flex flex-wrap gap-2 justify-center min-h-[48px] items-center">
                {ddPlacedItems.length === 0 ? (
                  <span className="text-xs text-slate-500">Tap items below to add to target</span>
                ) : (
                  ddPlacedItems.map((item, idx) => (
                    <button
                      key={idx}
                      disabled={isEvaluated}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setDdPlacedItems(prev => prev.filter((_, i) => i !== idx));
                      }}
                      className="relative z-10 pointer-events-auto bg-blue-600 text-white px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 border border-blue-400 shadow-md hover:bg-blue-700"
                    >
                      {item} <FiX size={12} />
                    </button>
                  ))
                )}
              </div>
            </div>

            <div className="space-y-2">
              <span className="text-xs font-bold text-slate-400">Available Items:</span>
              <div className="flex flex-wrap gap-2">
                {(content.items || []).map((item: string, idx: number) => {
                  const isPlaced = ddPlacedItems.includes(item);
                  return (
                    <button
                      key={idx}
                      disabled={isEvaluated || isPlaced}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setDdPlacedItems(prev => [...prev, item]);
                      }}
                      className={`relative z-10 pointer-events-auto px-3 py-1.5 rounded-lg text-xs font-semibold border transition ${isPlaced
                        ? 'bg-slate-800 border-slate-700 text-slate-500 cursor-not-allowed'
                        : 'bg-slate-750 border-slate-600 hover:border-slate-500 text-slate-200'
                        }`}
                    >
                      {item}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        );

      case 'summary':
        return (
          <div className="space-y-4">
            <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wide">
              Select the correct summary statement:
            </h3>
            {/* Statements are randomized for rendering but we know index 0 is correct in DB */}
            <div className="space-y-2">
              {(content.statements || []).map((stmt: string, idx: number) => {
                const isSelected = summarySelected === stmt;
                return (
                  <button
                    key={idx}
                    disabled={isEvaluated}
                    onClick={() => setSummarySelected(stmt)}
                    className={`w-full text-left p-3 rounded-xl border text-sm font-semibold flex items-center justify-between transition ${isSelected
                      ? 'bg-blue-600/35 border-blue-500 text-white'
                      : 'bg-slate-800/40 border-slate-700/60 hover:bg-slate-800 text-slate-300'
                      }`}
                  >
                    <span>{renderTextWithMath(stmt)}</span>
                  </button>
                );
              })}
            </div>
          </div>
        );

      case 'single_choice_set': {
        const questionsList = content.questions || [];
        const currentQ = questionsList[scIndex];
        if (!currentQ) return null;

        return (
          <div className="space-y-4">
            <div className="flex justify-between items-center text-xs font-bold text-slate-400">
              <span>Question {scIndex + 1} of {questionsList.length}</span>
              <span>Single Choice Set</span>
            </div>
            <h3 className="text-base font-extrabold text-white leading-relaxed">
              {renderTextWithMath(currentQ.question || '')}
            </h3>
            <div className="space-y-2">
              {(currentQ.options || []).map((opt: string, optIdx: number) => {
                const isSelected = scSelected[scIndex] === opt;
                return (
                  <button
                    key={optIdx}
                    disabled={isEvaluated}
                    onClick={() => {
                      setScSelected(prev => ({ ...prev, [scIndex]: opt }));
                      if (scIndex < questionsList.length - 1) {
                        setTimeout(() => setScIndex(prev => prev + 1), 300);
                      }
                    }}
                    className={`w-full text-left p-3 rounded-xl border text-sm font-semibold flex items-center justify-between transition ${isSelected
                      ? 'bg-blue-600/35 border-blue-500 text-white'
                      : 'bg-slate-800/40 border-slate-700/60 hover:bg-slate-800 text-slate-300'
                      }`}
                  >
                    <span>{renderTextWithMath(opt)}</span>
                  </button>
                );
              })}
            </div>
            <div className="flex justify-between border-t border-slate-800/50 pt-2">
              <button
                disabled={scIndex === 0}
                onClick={() => setScIndex(prev => prev - 1)}
                className="text-xs text-slate-400 hover:text-slate-200 disabled:opacity-40"
              >
                Previous
              </button>
              <button
                disabled={scIndex === questionsList.length - 1}
                onClick={() => setScIndex(prev => prev + 1)}
                className="text-xs text-slate-400 hover:text-slate-200 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        );
      }

      case 'mark_words': {
        const words = (content.question || '').split(/\s+/);
        return (
          <div className="space-y-3">
            <h3 className="text-xs font-bold text-slate-400">Click/Tap the correct words in the paragraph:</h3>
            <div className="bg-slate-850 border border-slate-700/60 p-4 rounded-xl text-base leading-loose text-slate-200 select-text flex flex-wrap gap-x-1.5 gap-y-1">
              {words.map((word: string, idx: number) => {
                const cleanWord = word.replace(/\*/g, '');
                const isSelected = mwSelected.has(idx);

                return (
                  <button
                    key={idx}
                    disabled={isEvaluated}
                    onClick={() => {
                      setMwSelected(prev => {
                        const next = new Set(prev);
                        if (next.has(idx)) {
                          next.delete(idx);
                        } else {
                          next.add(idx);
                        }
                        return next;
                      });
                    }}
                    className={`inline-block px-1 rounded font-semibold transition ${isSelected
                      ? 'bg-blue-600 text-white'
                      : 'bg-transparent text-slate-300 hover:bg-slate-800 border-b border-dashed border-slate-600'
                      }`}
                  >
                    {cleanWord}
                  </button>
                );
              })}
            </div>
          </div>
        );
      }

      case 'drag_text': {
        const parts = (content.question || '').split(/(\*[^*]+\*)/g);
        const blanks = parts.filter((_: any, idx: number) => idx % 2 === 1).map((s: string) => s.slice(1, -1).trim());
        let blankCount = 0;

        return (
          <div className="space-y-4">
            <h3 className="text-xs font-bold text-slate-400">Tap a blank space, then tap a word from below:</h3>
            <div className="bg-slate-800/20 border border-slate-850 p-4 rounded-xl text-base leading-loose text-slate-200">
              {parts.map((part: string, idx: number) => {
                if (part.startsWith('*') && part.endsWith('*')) {
                  const currentBlankIdx = blankCount;
                  blankCount++;
                  const placedVal = dtPlaced[currentBlankIdx] || '';

                  return (
                    <button
                      key={idx}
                      disabled={isEvaluated}
                      onClick={() => {
                        if (dtSelectedTag) {
                          setDtPlaced(prev => ({ ...prev, [currentBlankIdx]: dtSelectedTag }));
                          setDtSelectedTag(null);
                        } else if (placedVal) {
                          // remove
                          setDtPlaced(prev => {
                            const next = { ...prev };
                            delete next[currentBlankIdx];
                            return next;
                          });
                        }
                      }}
                      className={`mx-1.5 px-3 py-0.5 rounded border text-sm font-semibold min-w-[72px] transition ${isEvaluated
                        ? placedVal.toLowerCase() === part.slice(1, -1).trim().toLowerCase()
                          ? 'bg-green-950 border-green-500 text-green-300'
                          : 'bg-red-950 border-red-500 text-red-300'
                        : placedVal
                          ? 'bg-blue-600/20 border-blue-500 text-blue-300'
                          : 'border-dashed border-slate-600 hover:border-slate-500 bg-slate-850 text-slate-500'
                        }`}
                    >
                      {placedVal || 'Place here'}
                    </button>
                  );
                }
                return <span key={idx}>{renderTextWithMath(part)}</span>;
              })}
            </div>

            <div className="space-y-2">
              <span className="text-xs font-bold text-slate-400">Word tags:</span>
              <div className="flex flex-wrap gap-2">
                {blanks.map((tag: string, idx: number) => {
                  const isPlaced = Object.values(dtPlaced).includes(tag);
                  const isSelected = dtSelectedTag === tag;
                  return (
                    <button
                      key={idx}
                      disabled={isEvaluated || isPlaced}
                      onClick={() => setDtSelectedTag(isSelected ? null : tag)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition ${isSelected
                        ? 'bg-blue-600 border-blue-400 text-white'
                        : isPlaced
                          ? 'bg-slate-800 border-slate-700 text-slate-600 cursor-not-allowed'
                          : 'bg-slate-750 border-slate-600 hover:border-slate-500 text-slate-200'
                        }`}
                    >
                      {tag}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        );
      }

      case 'image':
        return (
          <div className="space-y-3 flex flex-col items-center">
            <img
              src={content.imageUrl}
              alt={content.alt || 'Visual interaction content'}
              className="max-w-full max-h-[220px] rounded-lg border border-slate-800 shadow-md object-contain"
            />
            {content.alt && (
              <p className="text-xs text-slate-400 italic text-center leading-relaxed">
                {content.alt}
              </p>
            )}
          </div>
        );

      case 'table':
        return (
          <div className="space-y-2">
            {content.title && (
              <h4 className="text-sm font-bold text-white mb-2">{content.title}</h4>
            )}
            <div className="overflow-x-auto border border-slate-800 rounded-xl">
              <table className="w-full text-xs text-left text-slate-300">
                <thead className="text-[10px] uppercase tracking-wider bg-slate-900 border-b border-slate-850 text-slate-400">
                  <tr>
                    {(content.headers || []).map((h: string, idx: number) => (
                      <th key={idx} className="px-4 py-2.5 font-bold">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-850/60 bg-slate-900/30">
                  {(content.rows || []).map((row: string[], rIdx: number) => (
                    <tr key={rIdx} className="hover:bg-slate-850/40">
                      {row.map((cell: string, cIdx: number) => (
                        <td key={cIdx} className="px-4 py-2 font-medium">{cell}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'label':
        return (
          <div className="p-3 bg-blue-900/10 border border-blue-500/20 text-blue-300 rounded-xl text-center font-bold text-sm tracking-wide">
            {renderTextWithMath(content.label || '')}
          </div>
        );

      case 'text':
        return (
          <div className="prose prose-invert max-w-none text-slate-300 text-sm leading-relaxed whitespace-pre-line bg-slate-850/30 border border-slate-800 p-4 rounded-xl">
            {renderTextWithMath(content.text || '')}
          </div>
        );

      default:
        return <p className="text-slate-400 text-xs">Unsupported overlay layout.</p>;
    }
  };

  // Construct watermark details
  const studentName = user ? `${user.firstName} ${user.lastName}` : 'Guest Student';
  const studentId = user?.id ? `ID: ${user.id.slice(0, 8)}` : '';
  const board = user?.attributes?.board || user?.attributes?.['custom:board'] || '';
  const standard = user?.attributes?.standard || user?.attributes?.['custom:standard'] || '';
  const educationMeta = [board, standard].filter(Boolean).join(' / ');
  const showSubmit = activeInteraction && !feedback?.isEvaluated &&
    !['image', 'table', 'label', 'text'].includes(activeInteraction.interactionType);

  const showRetry = activeInteraction && feedback?.isEvaluated && !feedback.isCorrect &&
    !['image', 'table', 'label', 'text'].includes(activeInteraction.interactionType);

  const showContinue = activeInteraction && (
    feedback?.isEvaluated ||
    ['image', 'table', 'label', 'text'].includes(activeInteraction.interactionType)
  );

  return (
    <div
      ref={containerRef}
      className={`relative w-full shadow-lg bg-black border border-gray-800 overflow-hidden group select-none ${isFullscreen ? 'h-screen w-screen fixed inset-0 z-50 rounded-none' : 'aspect-video rounded-xl'
        }`}
    >
      <style>{`
        video::-webkit-media-controls-fullscreen-button {
          display: none !important;
        }
      `}</style>
      <video
        ref={videoRef}
        controls
        controlsList="nodownload nofullscreen"
        className={`w-full h-full ${loading || error ? 'hidden' : 'block'} ${activeInteraction ? 'pointer-events-none' : ''}`}
        onContextMenu={(e) => e.preventDefault()}
        playsInline
        onTimeUpdate={handleTimeUpdate}
        onClick={handleVideoClick}
        onLoadedMetadata={handleLoadedMetadata}
        onPause={() => {
          if (videoRef.current) {
            const cur = videoRef.current.currentTime;
            const dur = videoRef.current.duration;
            saveProgress(cur, dur > 0 && cur >= dur - 5);
          }
        }}
      >
        Your browser does not support secure streaming playback.
      </video>

      {/* Floating Interactions & Control Bar */}
      {!loading && !error && (
        <div className="absolute top-3 right-3 bg-black/75 backdrop-blur-md rounded-lg p-1.5 border border-white/15 flex items-center space-x-2 pointer-events-auto z-30 opacity-70 hover:opacity-100 transition-opacity">
          {interactions.length > 0 && (
            <div className="flex items-center space-x-1.5 border-r border-white/15 pr-2">
              <span className="text-[9px] text-gray-400 font-extrabold uppercase tracking-wider px-1">Interactions:</span>
              {interactions.map((item, idx) => {
                const isInteracted = interactedIds.has(item.id);
                const isActive = activeInteraction?.id === item.id;
                return (
                  <button
                    key={item.id || idx}
                    onClick={() => {
                      if (videoRef.current) {
                        videoRef.current.currentTime = item.startTime;
                        if (videoRef.current.paused) {
                          videoRef.current.play();
                        }
                        setInteractedIds(prev => {
                          const next = new Set(prev);
                          next.delete(item.id);
                          return next;
                        });
                        setupInteraction(item);
                        if (item.pauseOnTrigger) {
                          videoRef.current.pause();
                        }
                      }
                    }}
                    title={`Jump to overlay at ${formatDuration(item.startTime)}`}
                    className={`w-5 h-5 rounded-full text-[9px] font-bold flex items-center justify-center transition-all ${isActive
                      ? 'bg-blue-500 text-white ring-2 ring-white/50'
                      : isInteracted
                        ? 'bg-green-600 text-white'
                        : 'bg-slate-700 text-slate-200 hover:bg-slate-600'
                      }`}
                  >
                    {idx + 1}
                  </button>
                );
              })}
            </div>
          )}
          <div className="flex items-center space-x-1 border-r border-white/15 pr-2">
            <button
              onClick={() => setResolution('720p')}
              className={`px-1.5 py-0.5 text-[10px] font-extrabold rounded transition ${resolution === '720p'
                ? 'bg-blue-600 text-white'
                : 'text-slate-400 hover:text-white hover:bg-slate-800'
                }`}
            >
              720p HD
            </button>
            <button
              onClick={() => setResolution('480p')}
              className={`px-1.5 py-0.5 text-[10px] font-extrabold rounded transition ${resolution === '480p'
                ? 'bg-blue-600 text-white'
                : 'text-slate-400 hover:text-white hover:bg-slate-800'
                }`}
            >
              480p SD
            </button>
          </div>
          <button
            onClick={toggleFullscreen}
            title={isFullscreen ? "Exit Fullscreen" : "Full Screen (Interactions Enabled)"}
            className="p-1 text-slate-300 hover:text-white transition-colors flex items-center gap-1 text-[10px] font-bold uppercase"
          >
            {isFullscreen ? <FiMinimize size={14} /> : <FiMaximize size={14} />}
          </button>
        </div>
      )}

      {/* Floating Interaction Button Overlay triggers (for button displayType) */}
      {!loading && !error && !activeInteraction && interactions.map((item, idx) => {
        const time = Math.floor(videoRef.current?.currentTime || 0);
        const isTriggered = time >= item.startTime && time <= item.endTime;
        const isInteracted = interactedIds.has(item.id);
        const isButtonMode = item.displayType === 'button';

        if (isTriggered && !isInteracted && isButtonMode) {
          return (
            <button
              key={item.id || idx}
              onClick={() => {
                setupInteraction(item);
                if (videoRef.current && !videoRef.current.paused) {
                  videoRef.current.pause();
                }
              }}
              className="absolute z-35 bg-blue-600 hover:bg-blue-500 text-white rounded-full px-3 py-1.5 shadow-lg flex items-center justify-center transition-transform hover:scale-105 pointer-events-auto border border-blue-400/30 font-bold text-[10px] uppercase tracking-wide gap-1 animate-pulse"
              style={{
                top: `${item.positionY}%`,
                left: `${item.positionX}%`,
                transform: 'translate(-50%, -50%)',
              }}
              title="Click to solve interactive prompt"
            >
              <FiHelpCircle size={12} />
              <span>{item.interactionType.replace('_', ' ')}</span>
            </button>
          );
        }
        return null;
      })}

      {/* Full Modal Poster Overlay */}
      {activeInteraction && (
        <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center z-45 p-4 pointer-events-auto">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-md max-h-[85%] overflow-y-auto p-5 text-white shadow-2xl relative flex flex-col space-y-4">

            {/* Header */}
            <div className="flex items-center justify-between border-b border-slate-800/80 pb-2">
              <div className="flex items-center space-x-2">
                <span className="text-[9px] font-bold uppercase tracking-wider bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded">
                  {activeInteraction.interactionType.replace('_', ' ')}
                </span>
                <span className="text-[10px] text-slate-400 font-mono">
                  At {formatDuration(activeInteraction.startTime)}
                </span>
              </div>
              <button
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSkip();
                }}
                className="text-xs text-slate-400 hover:text-white bg-slate-800 hover:bg-slate-700 px-2.5 py-1 rounded-lg font-bold transition text-white"
              >
                Skip Question
              </button>
            </div>

            {/* Main Interactive Questionnaire View */}
            <div className="pr-1">
              {renderInteractionContent()}
            </div>

            {/* Score/Results Feedback Box */}
            {feedback && (
              <div className={`p-3 rounded-xl flex items-center space-x-3 text-xs font-bold ${feedback.isCorrect
                ? 'bg-green-950/50 border border-green-500/30 text-green-300'
                : 'bg-red-950/50 border border-red-500/30 text-red-300'
                }`}>
                <div className="w-6 h-6 rounded-full flex items-center justify-center text-white bg-opacity-20 font-bold text-sm">
                  {feedback.isCorrect ? <FiCheck className="text-green-400" /> : <FiX className="text-red-400" />}
                </div>
                <div>
                  <p>{feedback.isCorrect ? 'Outstanding! Correct answer.' : 'Incorrect response. Try again to master this concept!'}</p>
                  <p className="text-[10px] opacity-70 mt-0.5">Score: {feedback.score} / {feedback.maxScore}</p>
                </div>
              </div>
            )}

            {/* Action Bar */}
            <div className="border-t border-slate-800 pt-3 flex items-center justify-between">
              <span className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider">
                Attempt {attemptNumber}
              </span>

              <div className="flex items-center space-x-2">
                {showRetry && (
                  <button
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleRetry();
                    }}
                    className="bg-slate-800 hover:bg-slate-750 text-xs px-3.5 py-1.5 rounded-lg font-bold transition flex items-center gap-1 border border-slate-700 text-white"
                  >
                    <FiRotateCcw size={12} />
                    <span>Try Again</span>
                  </button>
                )}

                {showSubmit && (
                  <button
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleSubmit();
                    }}
                    className="bg-blue-600 hover:bg-blue-500 text-xs px-4 py-1.5 rounded-lg font-bold transition shadow-md border border-blue-500 text-white"
                  >
                    Check
                  </button>
                )}

                {showContinue && (
                  <button
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleContinue();
                    }}
                    className="bg-green-600 hover:bg-green-500 text-xs px-4 py-1.5 rounded-lg font-bold transition shadow-md border border-green-500 flex items-center gap-1 text-white"
                  >
                    <span>Continue playback</span>
                    <FiChevronRight size={12} />
                  </button>
                )}
              </div>
            </div>

          </div>
        </div>
      )}

      {/* Loading overlay */}
      {loading && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black text-center p-6 space-y-4 rounded-xl z-20">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500" style={{ borderColor: '#3b82f6' }}></div>
          <p className="text-gray-400 text-sm">
            {downloadProgress > 0 ? `Securing video... ${downloadProgress}%` : 'Securing license handshake & buffering media...'}
          </p>
          {downloadProgress > 0 && (
            <div className="w-48 bg-gray-800 h-1.5 rounded-full overflow-hidden">
              <div
                className="bg-blue-500 h-full transition-all duration-300"
                style={{ width: `${downloadProgress}%` }}
              ></div>
            </div>
          )}
        </div>
      )}

      {/* Error overlay */}
      {error && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-950/95 text-center p-6 space-y-4 rounded-xl z-20 backdrop-blur-md border border-red-500/20">
          <div className="w-12 h-12 rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center text-2xl">
            🔒
          </div>
          <div className="space-y-1 max-w-md">
            <h4 className="text-white font-bold text-base">Access Restricted</h4>
            <p className="text-red-400 text-sm font-medium leading-relaxed">{error}</p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
            {resolution === '720p' && (
              <button
                onClick={() => setResolution('480p')}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold rounded-xl border border-slate-700 transition"
              >
                Try 480p SD Stream
              </button>
            )}
            <a
              href="/subscription"
              className="px-5 py-2 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded-xl shadow-lg shadow-blue-600/30 transition"
            >
              Upgrade / Subscribe Now
            </a>
          </div>
        </div>
      )}


      {/* Floating dynamic watermark */}
      <div
        className="absolute pointer-events-none text-white/25 text-[10px] sm:text-xs font-mono font-semibold tracking-wider whitespace-nowrap bg-black/10 px-2 py-1 rounded transition-all duration-1000 ease-in-out z-10"
        style={{
          top: watermarkPos.top,
          left: watermarkPos.left,
          textShadow: '1px 1px 2px rgba(0,0,0,0.8), -1px -1px 2px rgba(0,0,0,0.8)',
          pointerEvents: 'none'
        }}
      >
        <div>{studentName} {studentId}</div>
        <div>IP: {ipAddress}</div>
        {educationMeta && <div>{educationMeta}</div>}
      </div>
      {/* ── Floating AI Chat Widget (only visible while watching video) ── */}
    {bookInfo?.id != null && standardId != null && (
      <ChatWidget
        subjectId={bookInfo.subjectId as number}
        standardId={standardId}
        subjectName={bookInfo.subjectName || bookInfo.name}
      />
    )}
    </div>
  );
};

// Timer Helper
const formatDuration = (seconds: number) => {
  if (!seconds) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
};

export default SecureVideoPlayer;

